'use client';

import {
  makeAutoObservable,
  runInAction,
} from 'mobx';
import { z } from 'zod';

import { isDraftTabId } from './assistantTabsStore';
import {
  applyUsageDeltaToAgentStats,
  type AssistantAgentStats,
  type AssistantContextUsageEstimate,
  type AssistantUsage,
  createAssistantContextUsageEstimate,
  findCurrentConversationUsage,
  hasUsageFields,
  parseUsageFromTokenPayload,
} from './assistantTokenUsage';
import { normaliseAssistantTranscriptMessages } from './assistantTranscriptUtils';

import type { AssistantStore } from './assistantStore';
import type {
  AssistantMessage,
  AssistantRunStatusEvent,
  AssistantStepEvent,
  AssistantSummaryEvent,
  AssistantToolCall,
  AssistantUiState,
} from './assistantTypes';
import type {
  GatewayAgentInfo,
  GatewayChangedFile,
  GatewayEvent,
  GatewayStatus,
  GatewayTranscriptMessage,
} from '@/app/sdk';

import {
  extractToolNameFromPayload,
  isPlaceholderToolName,
} from '@/lib/ai/resolveToolDisplayName';

/** Key for the conversationsByKey map: agentId for real agents, draft tabId for drafts. */
export type ConversationKey = string;

/**
 * Per-conversation in-memory state. All streaming, run, and message state lives here.
 * Multiple conversations can stream concurrently — one runtime per open tab.
 */
export type ConversationRuntime = {
  key: ConversationKey;
  /** Resolved Cursor agent id; null until a draft adopts one. */
  agentId: string | null;
  messages: AssistantMessage[];
  assistantState: AssistantUiState;
  activeRunId: number | null;
  hasPendingRun: boolean;
  streamingMessageId: string | null;
  ignoredRunIds: Set<number>;
  /** Highest delta seq applied; used by reload re-attach to drop already-seen deltas. */
  lastAppliedDeltaSeq: number;
  /** True once an SDK transcript (or draft restore) has populated messages at least once. */
  hydrated: boolean;
  stats: AssistantAgentStats;
};

const ASSISTANT_NEW_AGENT_LABEL = 'New Agent';

type ActiveRunSnapshotTool = {
  id?: string;
  name?: string;
  status?: 'running' | 'completed' | 'error';
  args?: unknown;
  result?: unknown;
};

type ActiveRunSnapshot = {
  agentId: string;
  runId: number;
  deltaSeq?: number;
  assistantText?: string;
  thinking?: string;
  tools: ActiveRunSnapshotTool[];
};

const ACTIVE_RUN_SNAPSHOT_TOOL_SCHEMA = z.object({
  id: z.string().trim().min(1).optional(),
  name: z.string().trim().min(1).optional(),
  status: z.enum(['running', 'completed', 'error']).optional(),
  args: z.unknown().optional(),
  result: z.unknown().optional(),
}).passthrough();

const ACTIVE_RUN_SNAPSHOT_SCHEMA = z.object({
  agentId: z.string().trim().min(1),
  runId: z.number().finite(),
  deltaSeq: z.number().finite().optional(),
  assistantText: z.string().optional(),
  thinking: z.string().optional(),
  tools: z.array(ACTIVE_RUN_SNAPSHOT_TOOL_SCHEMA).optional().default([]),
}).passthrough();

// Flip to true locally to trace every gateway event through the renderer pipeline.
// Must remain false in committed code.
const VERBOSE_ASSISTANT_LOG = false && process.env.NODE_ENV === 'development';

function verboseLog(message: string, meta?: Record<string, unknown>): void {
  if (!VERBOSE_ASSISTANT_LOG) {
    return;
  }
  console.debug(`[assistant] ${message}`, meta ?? {});
}

let messageCounter = 0;

function nextMessageId(): string {
  messageCounter += 1;
  return `assistant-msg-${messageCounter}`;
}

function createEmptyAgentStats(): AssistantAgentStats {
  return {
    accumulatedInputTokens: 0,
    accumulatedOutputTokens: 0,
    accumulatedCachedInputTokens: 0,
    accumulatedCachedOutputTokens: 0,
    accumulatedTotalCost: 0,
  };
}

function parseActiveRunSnapshots(rawSnapshots: unknown): { snapshots: ActiveRunSnapshot[]; droppedCount: number } {
  if (!Array.isArray(rawSnapshots)) {
    return { snapshots: [], droppedCount: 0 };
  }
  const snapshots: ActiveRunSnapshot[] = [];
  let droppedCount = 0;
  for (const rawSnapshot of rawSnapshots) {
    const parsedSnapshot = ACTIVE_RUN_SNAPSHOT_SCHEMA.safeParse(rawSnapshot);
    if (!parsedSnapshot.success) {
      droppedCount += 1;
      continue;
    }
    snapshots.push(parsedSnapshot.data);
  }
  return { snapshots, droppedCount };
}

function isAutoGeneratedAgentLabel(label: string): boolean {
  const normalised = label.trim().toLowerCase();
  if (!normalised) {
    return true;
  }
  return normalised === ASSISTANT_NEW_AGENT_LABEL.toLowerCase()
    || normalised.startsWith('draft:')
    || normalised.startsWith('session ')
    || normalised === 'untitled chat';
}

function generateAutoAgentName(messageText: string): string {
  // Use the first non-empty line to keep names short and readable.
  const firstLine = messageText
    .split('\n')
    .map(line => line.trim())
    .find(Boolean) ?? '';
  const text = firstLine || messageText.trim();
  if (!text) return 'Untitled Chat';
  const collapsed = text.replace(/\s+/g, ' ').trim();
  const maxLength = 72;
  return collapsed.length <= maxLength ? collapsed : `${collapsed.slice(0, maxLength - 1).trimEnd()}…`;
}

export class AssistantConversationStore {
  conversationsByKey = new Map<ConversationKey, ConversationRuntime>();

  private pendingAutoNamePrompt: string | null = null;
  isHydratingByKey = new Map<ConversationKey, boolean>();

  constructor(private readonly root: AssistantStore) {
    makeAutoObservable(this, {
      messages: false,
      assistantState: false,
      agentStats: false,
      runningAgentIds: false,
    }, { autoBind: true });
  }

  // ---------------------------------------------------------------------------
  // Computed getters — delegate to the active ConversationRuntime
  // ---------------------------------------------------------------------------

  /** Messages for the currently active conversation tab. */
  get messages(): AssistantMessage[] {
    return this.activeConversationRuntime?.messages ?? [];
  }

  /** UI state of the currently active conversation. */
  get assistantState(): AssistantUiState {
    return this.activeConversationRuntime?.assistantState ?? 'idle';
  }

  /** Token-usage stats for the currently active conversation. */
  get agentStats(): AssistantAgentStats {
    return this.activeConversationRuntime?.stats ?? createEmptyAgentStats();
  }

  /**
   * Set of all agent ids that currently have a running conversation.
   * Derived from conversationsByKey so there is exactly one source of truth.
   */
  get runningAgentIds(): Set<string> {
    const running = new Set<string>();
    for (const rt of this.conversationsByKey.values()) {
      if (rt.assistantState === 'running' && rt.agentId) {
        running.add(rt.agentId);
      }
    }
    return running;
  }

  /** Stable array for React props; recomputes only when {@link runningAgentIds} changes. */
  get runningAgentIdsList(): string[] {
    const running: string[] = [];
    for (const rt of this.conversationsByKey.values()) {
      if (rt.assistantState === 'running' && rt.agentId) {
        running.push(rt.agentId);
      }
    }
    return running;
  }

  get isThinking(): boolean {
    const rt = this.activeConversationRuntime;
    return (rt?.assistantState ?? 'idle') === 'running' && !rt?.streamingMessageId;
  }

  get isStreaming(): boolean {
    const rt = this.activeConversationRuntime;
    return (rt?.assistantState ?? 'idle') === 'running' && !!rt?.streamingMessageId;
  }

  get usedContextTokensEstimate(): number {
    return this.currentConversationContextUsageEstimate.usedContextTokens;
  }

  get currentConversationContextUsageEstimate(): AssistantContextUsageEstimate {
    const rt = this.activeConversationRuntime;
    return createAssistantContextUsageEstimate(
      findCurrentConversationUsage(rt?.messages ?? [], rt?.activeRunId ?? null),
    );
  }

  get activeRunId(): number | null {
    return this.activeConversationRuntime?.activeRunId ?? null;
  }

  get hasPendingRun(): boolean {
    return this.activeConversationRuntime?.hasPendingRun ?? false;
  }

  private get activeConversationRuntime(): ConversationRuntime | undefined {
    const key = this.resolveActiveConversationKey();
    return key ? this.conversationsByKey.get(key) : undefined;
  }

  async reattachActiveRunSnapshots(): Promise<void> {
    const gateway = window.electronAPI?.gateway;
    if (!gateway || !('getActiveRunSnapshots' in gateway)) {
      return;
    }
    let rawSnapshots;
    try {
      rawSnapshots = await (gateway as { getActiveRunSnapshots: () => Promise<unknown> }).getActiveRunSnapshots();
    } catch {
      // Snapshot is a best-effort; do not block sync on failure.
      return;
    }
    const { snapshots, droppedCount } = parseActiveRunSnapshots(rawSnapshots);
    if (droppedCount > 0) {
      verboseLog('ignored invalid active-run snapshot entries', { droppedCount });
    }
    if (snapshots.length === 0) {
      return;
    }
    runInAction(() => {
      for (const snap of snapshots) {
        const agentId = snap.agentId;
        const runId = snap.runId;
        const rt = this.getOrCreateConversation(agentId, agentId);
        // Seed the runtime with the snapshot content so the streaming message
        // can continue without re-applying already-seen deltas.
        rt.activeRunId = runId;
        rt.hasPendingRun = false;
        rt.assistantState = 'running';
        rt.lastAppliedDeltaSeq = typeof snap.deltaSeq === 'number' ? snap.deltaSeq : -1;
        // Add the snapshot text as an initial streaming message if not already present
        if (rt.messages.length === 0 && snap.assistantText) {
          const id = nextMessageId();
          rt.streamingMessageId = id;
          rt.messages.push({
            id,
            role: 'assistant',
            content: snap.assistantText,
            thinking: snap.thinking || undefined,
            isStreaming: true,
            runId,
            tools: snap.tools.map(tool => {
              return {
                id: tool.id ?? 'unknown',
                name: tool.name ?? 'tool_call',
                status: (tool.status === 'running' || tool.status === 'error' ? tool.status : 'completed') as 'running' | 'completed' | 'error',
                args: tool.args,
                result: tool.result,
              };
            }),
            blocks: [],
          });
        }
        verboseLog('reattached to active run from snapshot', {
          agentId,
          runId,
          deltaSeq: rt.lastAppliedDeltaSeq,
        });
      }
    });
  }

  clearMessages(): void {
    runInAction(() => {
      const activeKey = this.resolveActiveConversationKey();
      if (activeKey) {
        this.resetConversationRuntime(activeKey);
      }
    });
  }

  flushPendingAutoName(): void {
    if (!this.pendingAutoNamePrompt || !this.root.activeAgentId) {
      return;
    }
    const prompt = this.pendingAutoNamePrompt;
    this.pendingAutoNamePrompt = null;
    void this.autoNameAgentIfDraft(prompt);
  }

  setPendingAutoNamePrompt(prompt: string | null): void {
    this.pendingAutoNamePrompt = prompt;
  }

  clearPendingAutoNamePrompt(): void {
    this.pendingAutoNamePrompt = null;
  }

  clearAllConversations(): void {
    this.conversationsByKey.clear();
    this.isHydratingByKey.clear();
    this.pendingAutoNamePrompt = null;
  }

  deleteConversation(key: ConversationKey): void {
    this.conversationsByKey.delete(key);
    this.isHydratingByKey.delete(key);
  }

  getOrCreateConversation(key: ConversationKey, agentId: string | null): ConversationRuntime {
    const existing = this.conversationsByKey.get(key);
    if (existing) {
      return existing;
    }
    const rt: ConversationRuntime = {
      key,
      agentId,
      messages: [],
      assistantState: 'idle',
      activeRunId: null,
      hasPendingRun: false,
      streamingMessageId: null,
      ignoredRunIds: new Set(),
      lastAppliedDeltaSeq: -1,
      hydrated: false,
      stats: createEmptyAgentStats(),
    };
    this.conversationsByKey.set(key, rt);
    return rt;
  }

  /** Maps an event's agentId to the conversation key that owns that agent. */
  resolveConversationKey(agentId?: string | null): ConversationKey | null {
    const trimmed = agentId?.trim();
    if (!trimmed) {
      // No agentId on the event — fall back to the active conversation key
      return this.resolveActiveConversationKey();
    }
    // Check if there is already a runtime for this agent (covers active + background tabs)
    if (this.conversationsByKey.has(trimmed)) {
      return trimmed;
    }
    // Check if this is the currently active agent
    if (trimmed === this.root.activeAgentId?.trim()) {
      return this.resolveActiveConversationKey();
    }
    // Create a background runtime for an agent we haven't opened yet
    return trimmed;
  }

  resolveActiveConversationKey(): ConversationKey | null {
    const activeTabId = this.root.tabs.getActiveTabId();
    return activeTabId || null;
  }

  resetConversationRuntime(key: ConversationKey): void {
    const rt = this.conversationsByKey.get(key);
    if (!rt) return;
    rt.messages = [];
    rt.stats = createEmptyAgentStats();
    rt.streamingMessageId = null;
    rt.activeRunId = null;
    rt.hasPendingRun = false;
    rt.ignoredRunIds.clear();
    rt.hydrated = false;
    rt.lastAppliedDeltaSeq = -1;
    this.pendingAutoNamePrompt = null;
  }

  markRuntimeRunning(rt: ConversationRuntime, runId?: number): void {
    rt.assistantState = 'running';
    if (runId !== undefined) {
      rt.activeRunId = runId;
      rt.hasPendingRun = false;
    } else {
      rt.hasPendingRun = true;
    }
  }

  markRuntimeIdle(rt: ConversationRuntime): void {
    const previousState = rt.assistantState;
    rt.assistantState = previousState === 'error' ? 'error' : 'idle';
    rt.activeRunId = null;
    rt.hasPendingRun = false;
  }

  /**
   * When agents are loaded from the gateway, update any runtimes that are
   * reported as running by the gateway but whose local state hasn't been set yet.
   * This handles cases where we reload mid-run.
   */
  syncRuntimeStatesFromAgents(agents: GatewayAgentInfo[]): void {
    for (const agent of agents) {
      const agentId = agent.agentId.trim();
      if (!agentId || !agent.isRunning) continue;
      const rt = this.conversationsByKey.get(agentId);
      if (rt && rt.assistantState !== 'running') {
        rt.assistantState = 'running';
      }
    }
  }

  resolveEventAgentId(agentId?: string): string | null {
    const trimmedAgentId = agentId?.trim();
    return trimmedAgentId?.length ? trimmedAgentId : null;
  }

  shouldTreatAsChatRunning(options: {
    state: GatewayStatus['state'];
    activeRunId?: number;
    eventAgentId?: string | null;
  }): boolean {
    if (options.state === 'error') {
      return false;
    }
    const activeKey = this.resolveActiveConversationKey();
    const activeRt = activeKey ? this.conversationsByKey.get(activeKey) : undefined;
    if (activeRt?.hasPendingRun) {
      return true;
    }
    if (options.state !== 'running') {
      return false;
    }
    const eventAgentId = options.eventAgentId?.trim();
    const currentAgentId = this.root.activeAgentId?.trim();
    if (eventAgentId) {
      if (currentAgentId && eventAgentId !== currentAgentId) {
        return false;
      }
      if (options.activeRunId !== undefined && Number.isFinite(options.activeRunId)) {
        return true;
      }
      return this.isAgentMarkedRunning(eventAgentId);
    }
    if (!currentAgentId) {
      return false;
    }
    return this.isAgentMarkedRunning(currentAgentId);
  }

  async hydrateAgentTranscript(agentId: string | null): Promise<boolean> {
    const gateway = window.electronAPI?.gateway;
    const projectPath = this.root.lastProjectPath;
    const trimmedAgentId = agentId?.trim();
    if (!gateway || !projectPath || !trimmedAgentId) {
      return false;
    }

    if (this.isHydratingByKey.get(trimmedAgentId)) {
      return false;
    }

    // Never overwrite a live or pending run with history from the SDK.
    const rt = this.conversationsByKey.get(trimmedAgentId);
    if (rt?.activeRunId !== null && rt?.activeRunId !== undefined) {
      return false;
    }
    if (rt?.hasPendingRun) {
      return false;
    }

    this.isHydratingByKey.set(trimmedAgentId, true);
    try {
      const transcript = await gateway.loadAgentTranscript(trimmedAgentId, projectPath);
      verboseLog('hydrateAgentTranscript: loaded transcript', {
        agentId: trimmedAgentId,
        messageCount: transcript.length,
        roles: [...new Set(transcript.map(m => m.role))],
      });
      let hydrated = false;
      runInAction(() => {
        // Abort if a run started while loading.
        const currentRt = this.getOrCreateConversation(trimmedAgentId, trimmedAgentId);
        if (currentRt.activeRunId !== null || currentRt.hasPendingRun) {
          return;
        }
        // Deterministic rule: SDK is authoritative for non-running conversations.
        const hydratedMessages = normaliseAssistantTranscriptMessages(transcript);
        this.bumpMessageCounter(hydratedMessages);
        currentRt.messages = hydratedMessages;
        this.recomputeStatsForRuntime(currentRt);
        currentRt.streamingMessageId = null;
        currentRt.hydrated = true;
        hydrated = true;
      });
      return hydrated;
    } catch {
      // ignore transcript hydration failures
      return false;
    } finally {
      this.isHydratingByKey.delete(trimmedAgentId);
    }
  }

  isHydratingConversation(key: ConversationKey): boolean {
    return this.isHydratingByKey.get(key) ?? false;
  }

  hasPendingDraftLiveRun(): boolean {
    for (const [key, rt] of this.conversationsByKey) {
      if (!isDraftTabId(key)) {
        continue;
      }
      if (this.runtimeHasLiveRun(rt)) {
        return true;
      }
    }
    return false;
  }

  rekeyPendingDraftRuntimeToAgent(draftKey: ConversationKey | null, agentId: string): void {
    const trimmedAgentId = agentId.trim();
    if (!trimmedAgentId) {
      return;
    }
    if (draftKey && isDraftTabId(draftKey) && draftKey !== trimmedAgentId) {
      this.rekeyDraftRuntime(draftKey, trimmedAgentId);
      return;
    }
    for (const [key, rt] of this.conversationsByKey) {
      if (!isDraftTabId(key) || key === trimmedAgentId) {
        continue;
      }
      if (this.runtimeHasLiveRun(rt)) {
        this.rekeyDraftRuntime(key, trimmedAgentId);
        return;
      }
    }
  }

  rekeyDraftRuntime(draftKey: ConversationKey, agentId: string): void {
    const draftRt = this.conversationsByKey.get(draftKey);
    if (!draftRt || draftKey === agentId) {
      return;
    }
    if (!this.runtimeHasLiveRun(draftRt)) {
      return;
    }
    draftRt.key = agentId;
    draftRt.agentId = agentId;
    this.conversationsByKey.set(agentId, draftRt);
    this.conversationsByKey.delete(draftKey);
    verboseLog('agent adopt: re-keyed draft runtime to agent id', {
      eventAgentId: agentId,
      draftKey,
      activeRunId: draftRt.activeRunId ?? undefined,
      hasPendingRun: draftRt.hasPendingRun,
    });
  }

  private runtimeHasLiveRun(rt: ConversationRuntime): boolean {
    return rt.hasPendingRun || rt.activeRunId !== null || rt.assistantState === 'running';
  }

  toTranscriptMessage(message: AssistantMessage): GatewayTranscriptMessage {
    return {
      id: message.id,
      role: message.role,
      content: message.content,
      runId: message.runId,
      thinking: message.thinking,
      thinkingDurationMs: message.thinkingDurationMs,
      tokenProgress: this.toSerializableValue(message.tokenProgress),
      steps: message.steps?.map(step => ({
        status: step.status,
        stepId: step.stepId,
        stepDurationMs: step.stepDurationMs,
        step: this.toSerializableValue(step.step),
      })),
      summaries: message.summaries?.map(summary => ({
        status: summary.status,
        summary: summary.summary,
      })),
      shellOutputs: message.shellOutputs?.map(output => this.toSerializableValue(output)),
      shellOutputDroppedCount: message.shellOutputDroppedCount,
      lastUserMessageAppended: this.toSerializableValue(message.lastUserMessageAppended),
      usage: message.usage,
      tools: message.tools?.map(tool => ({
        id: tool.id,
        name: tool.name,
        status: tool.status,
        args: this.toSerializableValue(tool.args),
        result: this.toSerializableValue(tool.result),
      })),
      blocks: message.blocks?.map(block => (
        block.type === 'text'
          ? { type: 'text' as const, content: block.content }
          : { type: 'tool' as const, toolId: block.toolId }
      )),
      changedFiles: message.changedFiles?.map(file => ({
        path: file.path,
        status: file.status,
      })),
    };
  }

  toSerializableValue(value: unknown): unknown {
    if (value === undefined) {
      return undefined;
    }
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return String(value);
    }
  }

  bindOrValidateRunForRuntime(rt: ConversationRuntime, runId: number): boolean {
    if (rt.ignoredRunIds.has(runId)) {
      verboseLog('bindOrValidateRun: dropped (ignored run)', { runId });
      return false;
    }

    if (rt.activeRunId === null) {
      // Normal path: a prompt is pending and this is its first event.
      // Recovery path: state says we are running but hasPendingRun was lost
      // (e.g. a stray status:ready event cleared it) — adopt the run rather
      // than silently dropping its output.
      if (!rt.hasPendingRun && rt.assistantState !== 'running') {
        verboseLog('bindOrValidateRun: dropped (no pending run)', {
          runId,
          assistantState: rt.assistantState,
        });
        return false;
      }
      if (!rt.hasPendingRun) {
        verboseLog('bindOrValidateRun: recovered run while running', { runId });
      }
      rt.activeRunId = runId;
      rt.hasPendingRun = false;
      rt.assistantState = 'running';
      return true;
    }

    const matches = rt.activeRunId === runId;
    if (!matches) {
      verboseLog('bindOrValidateRun: dropped (runId mismatch)', {
        runId,
        activeRunId: rt.activeRunId,
      });
    }
    return matches;
  }

  appendAssistantDeltaToRuntime(rt: ConversationRuntime, delta: string, seq?: number): void {
    if (!delta) return;
    // The bridge emits a single shared seq across message + thinking deltas.
    // We rely on ordered delivery over the single gateway.event IPC channel.
    // Drop deltas already covered by the reload snapshot to prevent double-applying text.
    if (seq !== undefined && seq <= rt.lastAppliedDeltaSeq) {
      return;
    }
    if (seq !== undefined) {
      rt.lastAppliedDeltaSeq = seq;
    }

    if (!rt.streamingMessageId) {
      const id = nextMessageId();
      rt.streamingMessageId = id;
      rt.messages.push({
        id,
        role: 'assistant',
        content: '',
        isStreaming: true,
        runId: rt.activeRunId ?? undefined,
        tools: [],
        blocks: [],
      });
      const msg = rt.messages.at(-1)!;
      this.appendAssistantTextDelta(msg, delta);
      return;
    }

    const message = rt.messages.find(m => m.id === rt.streamingMessageId);
    if (message) {
      this.appendAssistantTextDelta(message, delta);
    }
  }

  appendThinkingDeltaToRuntime(rt: ConversationRuntime, runId: number, delta: string, seq?: number): void {
    if (!delta) return;
    // Shared seq with message deltas: ordered gateway.event delivery is required
    // so mixed thinking/message chunks are deduplicated correctly.
    // Drop thinking deltas already covered by the reload snapshot.
    if (seq !== undefined && seq <= rt.lastAppliedDeltaSeq) {
      return;
    }
    if (seq !== undefined) {
      rt.lastAppliedDeltaSeq = seq;
    }
    const message = this.findAssistantMessageForRuntime(rt, runId);
    if (message) {
      message.thinking = `${message.thinking ?? ''}${delta}`;
    }
  }

  applyThinkingCompletedToRuntime(rt: ConversationRuntime, runId: number, thinkingDurationMs: number | undefined): void {
    const message = this.findAssistantMessageForRuntime(rt, runId);
    if (!message) {
      return;
    }
    if (typeof thinkingDurationMs === 'number' && Number.isFinite(thinkingDurationMs)) {
      message.thinkingDurationMs = thinkingDurationMs;
    }
  }

  applyTokenProgressToRuntime(rt: ConversationRuntime, runId: number, tokens: unknown): void {
    const message = this.findAssistantMessageForRuntime(rt, runId);
    if (message) {
      message.tokenProgress = this.toSerializableValue(tokens);
    }
    const parsedUsage = parseUsageFromTokenPayload(tokens);
    if (parsedUsage) {
      this.applyUsageToRuntime(rt, runId, parsedUsage);
    }
  }

  appendRunStatusEventToRuntime(rt: ConversationRuntime, runId: number, runStatus: AssistantRunStatusEvent): void {
    const message = this.findAssistantMessageForRuntime(rt, runId);
    if (!message) {
      return;
    }

    const statuses = message.runStatuses ?? [];
    message.runStatuses = statuses;
    const statusText = runStatus.status?.trim();
    const messageText = runStatus.message?.trim();
    if (!statusText && !messageText) {
      return;
    }

    const last = statuses.at(-1);
    if (last && last.status === statusText && last.message === messageText) {
      return;
    }

    statuses.push({
      status: statusText ?? 'status',
      message: messageText,
    });
  }

  appendStepEventToRuntime(rt: ConversationRuntime, runId: number, step: AssistantStepEvent): void {
    const message = this.findAssistantMessageForRuntime(rt, runId);
    if (!message) {
      return;
    }
    const steps = message.steps ?? [];
    message.steps = steps;

    const last = steps.at(-1);
    if (
      last
      && last.status === step.status
      && last.stepId === step.stepId
      && last.stepDurationMs === step.stepDurationMs
    ) {
      return;
    }

    steps.push({
      status: step.status,
      stepId: step.stepId,
      stepDurationMs: step.stepDurationMs,
      step: this.toSerializableValue(step.step),
    });
  }

  appendSummaryEventToRuntime(rt: ConversationRuntime, runId: number, summary: AssistantSummaryEvent): void {
    const message = this.findAssistantMessageForRuntime(rt, runId);
    if (!message) {
      return;
    }
    const summaries = message.summaries ?? [];
    message.summaries = summaries;

    const nextSummaryText = summary.summary?.trim();
    const last = summaries.at(-1);
    if (
      last
      && last.status === summary.status
      && (last.summary?.trim() ?? '') === (nextSummaryText ?? '')
    ) {
      return;
    }

    summaries.push({
      status: summary.status,
      summary: nextSummaryText,
    });
  }

  appendShellOutputToRuntime(rt: ConversationRuntime, runId: number, event: unknown): void {
    const message = this.findAssistantMessageForRuntime(rt, runId);
    if (!message) {
      return;
    }

    const shellOutputs = message.shellOutputs ?? [];
    message.shellOutputs = shellOutputs;
    shellOutputs.push(this.toSerializableValue(event));

    // Prevent large shell streams from exhausting renderer memory.
    if (shellOutputs.length > 80) {
      const removed = shellOutputs.length - 80;
      shellOutputs.splice(0, removed);
      message.shellOutputDroppedCount = (message.shellOutputDroppedCount ?? 0) + removed;
    }
  }

  applyUserMessageAppendedToRuntime(rt: ConversationRuntime, runId: number, messagePayload: unknown): void {
    const message = this.findAssistantMessageForRuntime(rt, runId);
    if (!message) {
      return;
    }
    message.lastUserMessageAppended = this.toSerializableValue(messagePayload);
  }

  applyUsageToRuntime(rt: ConversationRuntime, runId: number, usage: AssistantUsage): void {
    if (!hasUsageFields(usage)) {
      return;
    }

    const message = this.findAssistantMessageForRuntime(rt, runId);
    const previousUsage = message?.usage;
    if (message) {
      message.usage = usage;
    }
    applyUsageDeltaToAgentStats(rt.stats, previousUsage, usage);
  }

  applyChangedFilesToRuntime(rt: ConversationRuntime, runId: number, changedFiles: GatewayChangedFile[]): void {
    if (changedFiles.length === 0) return;
    const message = this.findAssistantMessageForRuntime(rt, runId);
    if (message) {
      message.changedFiles = changedFiles;
    }
  }

  upsertToolCallToRuntime(rt: ConversationRuntime, runId: number, event: Extract<GatewayEvent, { type: 'tool_call' }>): void {
    const message = this.findAssistantMessageForRuntime(rt, runId);
    if (!message || !event.name) {
      return;
    }

    const resolvedFromArgs = extractToolNameFromPayload(event.args, {
      allowInference: isPlaceholderToolName(event.name),
    });
    const name = resolvedFromArgs && !isPlaceholderToolName(resolvedFromArgs)
      ? resolvedFromArgs
      : event.name;

    const tools = message.tools ?? [];
    message.tools = tools;
    const explicitCallId = event.callId?.trim();
    const id = this.resolveToolCallId(tools, name, explicitCallId);
    const status = event.status ?? 'running';
    const existing = tools.find(tool => tool.id === id);
    if (existing) {
      if (explicitCallId && existing.id !== explicitCallId) {
        this.relinkAssistantToolBlock(message, existing.id, explicitCallId);
        existing.id = explicitCallId;
      }
      existing.name = name;
      existing.status = status;
      existing.args = event.args ?? existing.args;
      existing.result = event.result ?? existing.result;
      return;
    }

    tools.push({
      id,
      name,
      status,
      args: event.args,
      result: event.result,
    });
    this.appendAssistantToolBlock(message, id);
  }

  appendSystemMessageToRuntime(rt: ConversationRuntime, content: string): void {
    rt.messages.push({
      id: nextMessageId(),
      role: 'system',
      content,
    });
  }

  finaliseStreamingMessageForRuntime(rt: ConversationRuntime): void {
    if (!rt.streamingMessageId) return;
    const message = rt.messages.find(m => m.id === rt.streamingMessageId);
    if (message) {
      message.isStreaming = false;
    }
    rt.streamingMessageId = null;
  }

  createUserMessage(content: string): AssistantMessage {
    return {
      id: nextMessageId(),
      role: 'user',
      content,
    };
  }

  private upsertLocalAgent(agentId: string, displayName: string): void {
    const now = Date.now();
    const existing = this.root.agents.find(a => a.agentId === agentId);
    if (existing) {
      this.root.agents = this.root.agents.map(a =>
        a.agentId === agentId ? { ...a, displayName } : a,
      );
    } else {
      // Insert a placeholder so agentTabs can resolve the label immediately.
      this.root.agents = [
        {
          agentId,
          displayName,
          projectPath: this.root.lastProjectPath ?? '',
          createdAt: now,
          updatedAt: now,
        },
        ...this.root.agents,
      ];
    }
  }

  private async autoNameAgentIfDraft(firstMessageText: string): Promise<void> {
    const agentId = this.root.activeAgentId;
    if (!agentId) return;

    const agent = this.root.agents.find(s => s.agentId === agentId);
    // Only auto-name agents that still use an auto-generated draft/default label.
    if (agent && !isAutoGeneratedAgentLabel(agent.displayName)) return;

    const autoName = generateAutoAgentName(firstMessageText);

    // Optimistically update the local agents list (or insert if missing) so the
    // tab label and history popover reflect the new name before the IPC round-trip.
    runInAction(() => {
      this.upsertLocalAgent(agentId, autoName);
    });

    const gateway = window.electronAPI?.gateway;
    if (!gateway) return;

    try {
      await gateway.renameAgent(agentId, autoName);
      await this.root.refreshAgents();
    } catch {
      // Auto-naming is best-effort; ignore failures silently.
    }
  }

  private bumpMessageCounter(messages: AssistantMessage[]): void {
    for (const message of messages) {
      const match = /^assistant-msg-(\d+)$/.exec(message.id);
      if (!match) {
        continue;
      }
      const parsed = Number(match[1]);
      if (!Number.isFinite(parsed)) {
        continue;
      }
      messageCounter = Math.max(messageCounter, parsed);
    }
  }

  private recomputeStatsForRuntime(rt: ConversationRuntime): void {
    rt.stats = createEmptyAgentStats();
    for (const message of rt.messages) {
      const usage = message.usage;
      if (!usage) {
        continue;
      }
      rt.stats.accumulatedInputTokens += usage.inputTokens ?? 0;
      rt.stats.accumulatedOutputTokens += usage.outputTokens ?? 0;
      rt.stats.accumulatedCachedInputTokens += usage.cacheReadTokens ?? 0;
      rt.stats.accumulatedCachedOutputTokens += usage.cacheWriteTokens ?? 0;
    }
  }

  private isAgentMarkedRunning(agentId: string): boolean {
    const trimmedAgentId = agentId.trim();
    if (!trimmedAgentId) {
      return false;
    }
    return this.root.agents.some(agent => agent.agentId.trim() === trimmedAgentId && agent.isRunning === true);
  }

  private findAssistantMessageForRuntime(rt: ConversationRuntime, runId: number): AssistantMessage | undefined {
    let message = rt.messages.find(item => item.role === 'assistant' && item.runId === runId);
    if (message) {
      return message;
    }

    if (!rt.streamingMessageId) {
      const id = nextMessageId();
      rt.streamingMessageId = id;
      message = {
        id,
        role: 'assistant',
        content: '',
        isStreaming: true,
        runId,
        tools: [],
        blocks: [],
      };
      rt.messages.push(message);
      return message;
    }

    message = rt.messages.find(item => item.id === rt.streamingMessageId);
    if (message) {
      message.runId = runId;
      message.tools ??= [];
      message.blocks ??= [];
    }
    return message;
  }

  private appendAssistantTextDelta(message: AssistantMessage, delta: string): void {
    if (!delta) {
      return;
    }
    message.content += delta;
    const blocks = message.blocks ?? [];
    message.blocks = blocks;
    const lastBlock = blocks.at(-1);
    if (lastBlock?.type === 'text') {
      lastBlock.content += delta;
      return;
    }
    blocks.push({ type: 'text', content: delta });
  }

  private appendAssistantToolBlock(message: AssistantMessage, toolId: string): void {
    const blocks = message.blocks ?? [];
    message.blocks = blocks;
    if (blocks.some(block => block.type === 'tool' && block.toolId === toolId)) {
      return;
    }
    blocks.push({ type: 'tool', toolId });
  }

  private resolveToolCallId(
    tools: AssistantToolCall[],
    toolName: string,
    explicitCallId: string | undefined,
  ): string {
    if (explicitCallId) {
      const existingWithCallId = tools.find(tool => tool.id === explicitCallId);
      if (existingWithCallId) {
        return explicitCallId;
      }
      const runningTool = this.findSingleRunningToolByName(tools, toolName);
      if (runningTool) {
        return runningTool.id;
      }
      return explicitCallId;
    }

    const runningTool = this.findSingleRunningToolByName(tools, toolName);
    if (runningTool) {
      return runningTool.id;
    }

    const fallbackIndex = tools.filter(tool => tool.name === toolName).length;
    return `${toolName}-${fallbackIndex}`;
  }

  private findSingleRunningToolByName(tools: AssistantToolCall[], toolName: string): AssistantToolCall | undefined {
    const runningTools = tools.filter(tool => tool.name === toolName && tool.status === 'running');
    return runningTools.length === 1 ? runningTools[0] : undefined;
  }

  private relinkAssistantToolBlock(message: AssistantMessage, fromToolId: string, toToolId: string): void {
    if (!message.blocks?.length || fromToolId === toToolId) {
      return;
    }
    for (const block of message.blocks) {
      if (block.type === 'tool' && block.toolId === fromToolId) {
        block.toolId = toToolId;
      }
    }
  }
}
